import React, { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Coins, DollarSign, Euro, Plus, Minus, Check, X, RefreshCw } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';

interface CurrencyUnit {
  id: string;
  name: string;
  value: number;
  icon: string;
  color: string;
  symbol: string;
}

interface CurrencyConfig {
  units: CurrencyUnit[];
  targetAmount: number;
  showTarget: boolean;
  allowInteraction: boolean;
  theme: 'primary' | 'secondary' | 'success' | 'warning';
  question?: string;
  changeTarget?: boolean;
}

interface CurrencyComponentProps {
  resource: {
    id?: string;
    type: string;
    name?: string;
    description?: string;
    config?: CurrencyConfig;
    units?: CurrencyUnit[];
    targetAmount?: number;
    showTarget?: boolean;
    allowInteraction?: boolean;
    theme?: 'primary' | 'secondary' | 'success' | 'warning';
    question?: string;
    changeTarget?: boolean;
    [key: string]: any;
  };
  onAction?: (action: string, data?: any) => void;
}

const getConfigValue = (resource: any, key: string, defaultValue: any) => {
  return resource.config?.[key] ?? resource[key] ?? defaultValue;
};

const getIconComponent = (iconName: string) => {
  const iconMap: { [key: string]: React.ComponentType<any> } = {
    coins: Coins,
    dollar: DollarSign,
    euro: Euro,
  };
  return iconMap[iconName] || Coins;
};

const getThemeColors = (theme: string) => {
  const themes = {
    primary: 'bg-blue-500 text-white',
    secondary: 'bg-gray-500 text-white',
    success: 'bg-green-500 text-white',
    warning: 'bg-yellow-500 text-white',
  };
  return themes[theme as keyof typeof themes] || themes.primary;
};

export const CurrencyComponent: React.FC<CurrencyComponentProps> = ({ resource, onAction }) => {
  const { t } = useTranslation();
  
  const [selectedUnits, setSelectedUnits] = useState<Record<string, number>>({});
  const [showResults, setShowResults] = useState(false);
  const [startTime, setStartTime] = useState<number>(Date.now());
  const units = getConfigValue(resource, 'units', [
    { id: '1', name: t('currency.coin1'), value: 1, icon: 'coins', color: 'bg-yellow-400', symbol: '1€' },
    { id: '2', name: t('currency.coin2'), value: 2, icon: 'coins', color: 'bg-yellow-400', symbol: '2€' },
    { id: '5', name: t('currency.bill5'), value: 5, icon: 'dollar', color: 'bg-green-400', symbol: '5€' },
    { id: '10', name: t('currency.bill10'), value: 10, icon: 'dollar', color: 'bg-green-400', symbol: '10€' },
  ]);

  const targetAmount = getConfigValue(resource, 'targetAmount', 15);
  const [currentTargetAmount, setCurrentTargetAmount] = useState<number>(targetAmount);
  const showTarget = getConfigValue(resource, 'showTarget', true);
  const allowInteraction = getConfigValue(resource, 'allowInteraction', true);
  const theme = getConfigValue(resource, 'theme', 'primary');
  const question = getConfigValue(resource, 'question', t('currency.defaultQuestion'));
  const changeTarget = getConfigValue(resource, 'changeTarget', true);
  const maxTarget = getConfigValue(resource, 'maxTarget', 25);
  const minTarget = getConfigValue(resource, 'minTarget', 5);
  const handleUnitClick = useCallback((unitId: string) => {
    if (!allowInteraction) return;
    
    setSelectedUnits(prev => ({
      ...prev,
      [unitId]: (prev[unitId] || 0) + 1
    }));
    setShowResults(false);
  }, [allowInteraction]);

  const handleUnitRemove = useCallback((unitId: string) => {
    if (!allowInteraction) return;
    
    setSelectedUnits(prev => {
      const newUnits = { ...prev };
      if (newUnits[unitId] > 0) {
        newUnits[unitId] -= 1;
        if (newUnits[unitId] === 0) {
          delete newUnits[unitId];
        }
      }
      return newUnits;
    });
    setShowResults(false);
  }, [allowInteraction]);

  const calculateTotal = useCallback(() => {
    return Object.entries(selectedUnits).reduce((total, [unitId, count]) => {
      const unit = units.find((u: any) => u.id === unitId);
      return total + (unit ? unit.value * count : 0);
    }, 0);
  }, [selectedUnits, units]);

  const isCorrect = useCallback(() => {
    return calculateTotal() === currentTargetAmount;
  }, [calculateTotal, currentTargetAmount]);

  const handleCheck = useCallback(() => {
    setShowResults(true);
    onAction?.('check', { duration: (Date.now() - startTime) / 1000, status: isCorrect(), score: isCorrect() ? 1 : 0 });
  }, [isCorrect, onAction, startTime]);

  const handleReset = useCallback(() => {
    setSelectedUnits({});
    setShowResults(false);
    setStartTime(Date.now());
  }, []);

  const handleChangeTarget = useCallback((newTarget: number) => {
    setCurrentTargetAmount(newTarget);
    setSelectedUnits({});
    setShowResults(false);
    setStartTime(Date.now());
  }, []);

  const total = calculateTotal();

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          {resource.name || t('currency.currencyCombination')}
        </h3>
        <p className="text-gray-600 mb-4">
          {resource.description || t('currency.createAmountWithCurrency')}
        </p>
        {question && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-blue-800 font-medium">{question}</p>
          </div>
        )}
      </div>

      {/* Target Amount */}
      {showTarget && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <h4 className="text-lg font-semibold text-green-800">
                {t('currency.targetAmount')}: {currentTargetAmount}€
              </h4>
              {allowInteraction && changeTarget && (
                <button
                  onClick={() => {
                    const newTarget = Math.floor(Math.random() * (maxTarget - minTarget + 1)) + minTarget; // Random target between minTarget and maxTarget
                    handleChangeTarget(newTarget);
                  }}
                  className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-lg transition-colors"
                  title={t('currency.newTarget')}
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Coins className="w-5 h-5 text-green-600" />
              <span className="text-sm text-green-700">{t('currency.useCurrencyUnits')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Currency Units */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-700 mb-4">{t('currency.availableUnits')}</h4>
        <div className="grid grid-cols-2 gap-4">
          {units.map((unit: any) => (
            <motion.div
              key={unit.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`
                p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 text-center
                ${allowInteraction ? 'hover:border-blue-300 hover:shadow-md' : ''}
                ${selectedUnits[unit.id] ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}
              `}
              onClick={() => handleUnitClick(unit.id)}
            >
              <div className={`w-12 h-12 ${unit.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                {React.createElement(getIconComponent(unit.icon), { className: 'w-6 h-6 text-white' })}
              </div>
              <h5 className="font-semibold text-gray-800 mb-1">{unit.name}</h5>
              <p className="text-lg font-bold text-gray-700">{unit.symbol}</p>
              {selectedUnits[unit.id] > 0 && (
                <div className="mt-2 flex items-center justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitRemove(unit.id);
                    }}
                    className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-lg font-bold text-blue-600">{selectedUnits[unit.id]}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitClick(unit.id);
                    }}
                    className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Current Total */}
      <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-800">{t('currency.currentTotal')}:</h4>
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-gray-700">{total}€</span>
            {total > 0 && (
              <div className={`w-4 h-4 rounded-full ${total === currentTargetAmount ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      {allowInteraction && (
        <div className="flex space-x-3 mb-6">
          <button
            onClick={handleCheck}
            disabled={total === 0}
            className={`px-6 py-3 rounded-lg text-white font-medium transition-colors ${
              total > 0 ? getThemeColors(theme) : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {t('currency.checkAnswer')}
          </button>
          <button
            onClick={handleReset}
            className="px-6 py-3 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
          >
            {t('currency.reset')}
          </button>
        </div>
      )}

      {/* Results */}
      {showResults && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className={`p-4 rounded-lg border-2 ${
            isCorrect() 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-center space-x-3 mb-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isCorrect() ? 'bg-green-500' : 'bg-red-500'
            }`}>
              {isCorrect() ? (
                <Check className="w-5 h-5 text-white" />
              ) : (
                <X className="w-5 h-5 text-white" />
              )}
            </div>
            <h4 className={`text-lg font-semibold ${
              isCorrect() ? 'text-green-800' : 'text-red-800'
            }`}>
              {isCorrect() ? t('currency.correct') : t('currency.incorrect')}
            </h4>
          </div>
          
          <div className="space-y-2">
            <p className={`text-sm ${isCorrect() ? 'text-green-700' : 'text-red-700'}`}>
              {isCorrect() 
                ? t('currency.correctMessage', { amount: currentTargetAmount.toString() })
                : t('currency.incorrectMessage', { current: total.toString(), target: currentTargetAmount.toString() })
              }
            </p>
            
            {Object.keys(selectedUnits).length > 0 && (
              <div className="mt-3">
                <h5 className="font-medium text-gray-800 mb-2">{t('currency.yourCombination')}:</h5>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedUnits).map(([unitId, count]) => {
                    const unit = units.find((u: any) => u.id === unitId);
                    return (
                      <div key={unitId} className="flex items-center space-x-1 bg-white px-2 py-1 rounded border">
                        <span className="text-sm font-medium">{unit?.symbol}</span>
                        <span className="text-sm text-gray-600">× {count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Instructions */}
      {!allowInteraction && (
        <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            💡 <strong>{t('currency.instructions')}:</strong> {t('currency.instructionsDescription')}
          </p>
        </div>
      )}
    </div>
  );
};
